import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Table,
  Button,
  Input,
  Select,
  Space,
  Tag,
  Modal,
  message,
  Card,
  Row,
  Col,
  Form,
  InputNumber,
  Popconfirm,
  Descriptions,
  Avatar,
  Tooltip,
  Badge,
  Statistic,
  Typography,
  List,
  Radio
} from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  CarOutlined,
  UserOutlined,
  FilterOutlined,
  SwapOutlined,
  RollbackOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import { all_routes } from '../../../router/all_routes';
import vehicleService from '../../../services/vehicle/vehicle.service';
import userService from '../../../services/user/user.service';
import {
  Vehicle,
  CreateVehicleDTO,
  UpdateVehicleDTO,
  VehicleType,
  VehicleStatus,
  AssignVehicleDTO,
  VehicleAssignment,
  VehicleAssignmentType,
  VehicleUsageHistoryItem,
  VehicleUsageActivityType,
  VehicleUsageActivityStatus
} from '../../../types/vehicle.types';
import { User } from '../../../types/user.types';
import { handleApiError } from '../../../utils/errorHandler';
import LoadingSpinner from '../../shared/uiInterface/base-ui/LoadingSpinner';
import FooterSection from '../../shared/components/footerSection';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

const { Option } = Select;
const { Title, Text } = Typography;

const POOL_HELPER_TEXT = 'Havuz araçları kimseye zimmetlenmez; uygun saatlerde tüm çalışanlar aktivitelerinde kullanabilir.';

const isPoolVehicle = (vehicle?: Pick<Vehicle, 'assignmentType'> | null) =>
  vehicle?.assignmentType === VehicleAssignmentType.POOL;

const USAGE_ACTIVITY_TYPE_LABELS: Record<VehicleUsageActivityType, string> = {
  VISIT: 'Ziyaret',
  DELIVERY: 'Teslimat',
  MAINTENANCE: 'Bakım',
  OTHER: 'Diğer',
  OFFICE: 'Ofis'
};

const USAGE_STATUS_TAGS: Record<VehicleUsageActivityStatus, { label: string; color?: string }> = {
  PENDING_APPROVAL: { label: 'Onay Bekliyor', color: 'gold' },
  PLANNED: { label: 'Planlandı', color: 'blue' },
  COMPLETED: { label: 'Tamamlandı', color: 'green' },
  CANCELLED: { label: 'İptal Edildi' },
  REJECTED: { label: 'Reddedildi', color: 'red' }
};

const USAGE_HISTORY_COLUMNS = [
  {
    title: 'Çalışan',
    key: 'user',
    render: (_: unknown, item: VehicleUsageHistoryItem) =>
      item.user ? `${item.user.firstName} ${item.user.lastName}` : '-'
  },
  {
    title: 'Aktivite',
    key: 'activity',
    render: (_: unknown, item: VehicleUsageHistoryItem) => (
      <div>
        <div>{item.title}</div>
        {item.type && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {USAGE_ACTIVITY_TYPE_LABELS[item.type] || item.type}
          </Text>
        )}
      </div>
    )
  },
  {
    title: 'Tarih',
    key: 'date',
    render: (_: unknown, item: VehicleUsageHistoryItem) => {
      const start = dayjs(item.startDate).format('DD.MM.YYYY HH:mm');
      return item.endDate ? (
        `${start} – ${dayjs(item.endDate).format('DD.MM.YYYY HH:mm')}`
      ) : (
        <span>
          {start} <Text type="secondary">(devam ediyor)</Text>
        </span>
      );
    }
  },
  {
    title: 'Durum',
    key: 'status',
    render: (_: unknown, item: VehicleUsageHistoryItem) => {
      const tag = USAGE_STATUS_TAGS[item.status];
      return tag ? <Tag color={tag.color}>{tag.label}</Tag> : <Tag>{item.status}</Tag>;
    }
  }
];

const VehiclesPage: React.FC = () => {
  const { t } = useTranslation();
  const routes = all_routes;

  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [filteredVehicles, setFilteredVehicles] = useState<Vehicle[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);

  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [assignModalVisible, setAssignModalVisible] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<VehicleStatus | undefined>(undefined);
  const [typeFilter, setTypeFilter] = useState<VehicleType | undefined>(undefined);

  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [assignForm] = Form.useForm();

  const [stats, setStats] = useState({
    total: 0,
    available: 0,
    assigned: 0,
    pool: 0,
    maintenance: 0
  });

  useEffect(() => {
    fetchVehicles();
    fetchUsers();
  }, []);

  useEffect(() => {
    filterVehicles();
    calculateStats();
  }, [vehicles, searchTerm, statusFilter, typeFilter]);

  const fetchVehicles = async () => {
    try {
      setLoading(true);
      const data = await vehicleService.getVehicles();
      setVehicles(data);
    } catch (error: any) {
      handleApiError(error, t);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const userData = await userService.getUsers();
      setUsers(userData);
    } catch (error: any) {
      handleApiError(error, t);
    }
  };

  const assignableUsers = users.filter((user) => user.status !== "resigned")

  const filterVehicles = () => {
    let filtered = [...vehicles];

    if (searchTerm) {
      filtered = filtered.filter(
        (vehicle) =>
          vehicle.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          vehicle.plateNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
          vehicle.brand.toLowerCase().includes(searchTerm.toLowerCase()) ||
          vehicle.model.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter) {
      filtered = filtered.filter((vehicle) => vehicle.status === statusFilter);
    }

    if (typeFilter) {
      filtered = filtered.filter((vehicle) => vehicle.type === typeFilter);
    }

    setFilteredVehicles(filtered);
  };

  const calculateStats = () => {
    // Havuz araçları zimmetlenemediği için "Atanabilir" sayısına dahil edilmez, ayrı gösterilir
    const available = vehicles.filter(
      (v) => v.status === VehicleStatus.ACTIVE && !isPoolVehicle(v) && !v.currentAssignment
    ).length;
    const assigned = vehicles.filter((v) => v.currentAssignment).length;
    const pool = vehicles.filter((v) => isPoolVehicle(v) && v.status === VehicleStatus.ACTIVE).length;
    const passive = vehicles.filter((v) => v.status === VehicleStatus.PASSIVE).length;

    setStats({
      total: vehicles.length,
      available,
      assigned,
      pool,
      maintenance: passive
    });
  };

  const handleCreateVehicle = async (values: any) => {
    try {
      const createData: CreateVehicleDTO = {
        name: values.name,
        plateNumber: values.plateNumber,
        brand: values.brand,
        model: values.model,
        year: values.year,
        type: values.type,
        assignmentType: values.assignmentType || VehicleAssignmentType.PERSONAL
      };

      await vehicleService.createVehicle(createData);
      message.success('Araç başarıyla eklendi!');
      
      setCreateModalVisible(false);
      createForm.resetFields();
      fetchVehicles();
    } catch (error: any) {
      handleApiError(error, t);
    }
  };

  const handleUpdateVehicle = async (values: any) => {
    if (!selectedVehicle) return;

    try {
      const updateData: UpdateVehicleDTO = values;
      await vehicleService.updateVehicle(selectedVehicle.id, updateData);
      message.success('Araç başarıyla güncellendi!');
      setEditModalVisible(false);
      editForm.resetFields();
      fetchVehicles();
    } catch (error: any) {
      handleApiError(error, t);
    }
  };

  const handleDeleteVehicle = async (id: number) => {
    try {
      await vehicleService.deleteVehicle(id);
      message.success('Araç başarıyla silindi!');
      fetchVehicles();
    } catch (error: any) {
      handleApiError(error, t);
    }
  };

  const handleAssignVehicle = async (values: any) => {
    if (!selectedVehicle) return;

    try {
      const assignData: AssignVehicleDTO = {
        assigneeId: values.assigneeId,
        odometer: values.odometer
      };

      await vehicleService.assignVehicle(selectedVehicle.id, assignData);
      message.success('Araç başarıyla zimmetlendi!');
      setAssignModalVisible(false);
      assignForm.resetFields();
      fetchVehicles();
    } catch (error: any) {
      handleApiError(error, t);
    }
  };

  const handleReturnVehicle = async (id: number) => {
    try {
      await vehicleService.returnVehicle(id);
      message.success('Araç başarıyla iade alındı!');
      fetchVehicles();
    } catch (error: any) {
      handleApiError(error, t);
    }
  };

  const getVehicleTypeLabel = (type: VehicleType) => {
    const labels = {
      [VehicleType.CAR]: 'Otomobil',
      [VehicleType.VAN]: 'Minibüs',
      [VehicleType.MOTORCYCLE]: 'Motosiklet',
      [VehicleType.TRUCK]: 'Kamyon'
    };
    return labels[type] || type;
  };

  const getStatusTag = (status: VehicleStatus) => {
    const config = {
      [VehicleStatus.ACTIVE]: { color: 'green', label: 'Atanabilir' },
      [VehicleStatus.PASSIVE]: { color: 'orange', label: 'Atanamaz' },
      [VehicleStatus.SCRAPPED]: { color: 'red', label: 'Hurdaya Ayrıldı' },
      [VehicleStatus.DELETED]: { color: 'default', label: 'Silindi' }
    };
    const { color, label } = config[status] || { color: 'default', label: status };
    return <Tag color={color}>{label}</Tag>;
  };

  const columns = [
    {
      title: 'Araç Adı',
      dataIndex: 'name',
      key: 'name',
      render: (text: string) => (
        <Text strong style={{ fontSize: '14px' }}>{text}</Text>
      )
    },
    {
      title: 'Plaka',
      dataIndex: 'plateNumber',
      key: 'plateNumber',
      render: (text: string) => (
        <Text style={{ fontSize: '13px' }}>{text}</Text>
      )
    },
    {
      title: 'Marka / Model',
      key: 'brandModel',
      render: (record: Vehicle) => (
        <div>
          <div>{record.brand}</div>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.model} ({record.year})
          </Text>
        </div>
      )
    },
    {
      title: 'Tip',
      dataIndex: 'type',
      key: 'type',
      render: (type: VehicleType) => getVehicleTypeLabel(type)
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        // Backend'den gelen status'u VehicleStatus enum'ına map et
        const statusMap: { [key: string]: VehicleStatus } = {
          'active': VehicleStatus.ACTIVE,
          'passive': VehicleStatus.PASSIVE,
          'scrapped': VehicleStatus.SCRAPPED,
          'deleted': VehicleStatus.DELETED,
        };
        const mappedStatus = statusMap[status] || status as VehicleStatus;
        return getStatusTag(mappedStatus);
      }
    },
    {
      title: 'Zimmet Durumu',
      key: 'assignment',
      render: (record: Vehicle) => {
        if (isPoolVehicle(record)) {
          return (
            <Tooltip title={POOL_HELPER_TEXT}>
              <Tag color="purple">Havuz</Tag>
            </Tooltip>
          );
        }
        const assignee = record.currentAssignment?.assignee || record.currentAssignment?.assignedTo;
        if (record.currentAssignment && assignee) {
          return (
            <Space>
              <Avatar style={{ backgroundColor: '#87d068' }}>
                {assignee.firstName[0]}{assignee.lastName[0]}
              </Avatar>
              <div>
                <Tag color="blue">Zimmetli</Tag>
                <div style={{ fontSize: '13px' }}>
                  {assignee.firstName} {assignee.lastName}
                </div>
                <Text type="secondary" style={{ fontSize: '11px' }}>
                  {dayjs(record.currentAssignment.assignedAt).format('DD.MM.YYYY')}
                </Text>
              </div>
            </Space>
          );
        }
        return <Tag color="green">-</Tag>;
      }
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (record: Vehicle) => (
        <Space size="small">
          <Tooltip title="Detaylar">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={async () => {
                setSelectedVehicle(record);
                setDetailsModalVisible(true);
                setDetailsLoading(true);
                try {
                  const detailed = await vehicleService.getVehicleById(record.id);
                  // Modal kapatıldıysa veya başka araç seçildiyse sonucu yok say
                  setSelectedVehicle((prev) => (prev?.id === record.id ? detailed : prev));
                } catch (error) {
                  handleApiError(error);
                } finally {
                  setDetailsLoading(false);
                }
              }}
            />
          </Tooltip>
          <Tooltip title="Düzenle">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => {
                setSelectedVehicle(record);
                editForm.setFieldsValue({
                  ...record,
                  assignmentType: record.assignmentType || VehicleAssignmentType.PERSONAL
                });
                setEditModalVisible(true);
              }}
            />
          </Tooltip>
          {record.status === VehicleStatus.ACTIVE && !record.currentAssignment && !isPoolVehicle(record) && (
            <Tooltip title="Zimmetle">
              <Button
                type="text"
                icon={<SwapOutlined />}
                onClick={() => {
                  setSelectedVehicle(record);
                  assignForm.setFieldsValue({ odometer: record.currentOdometer || 0 });
                  setAssignModalVisible(true);
                }}
              />
            </Tooltip>
          )}
          {record.currentAssignment && (
            <Popconfirm
              title="Araç iadesini almak istediğinizden emin misiniz?"
              onConfirm={() => handleReturnVehicle(record.id)}
              okText="Evet"
              cancelText="Hayır"
            >
              <Tooltip title="İade Al">
                <Button type="text" icon={<RollbackOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
          <Popconfirm
            title="Bu aracı silmek istediğinizden emin misiniz?"
            onConfirm={() => handleDeleteVehicle(record.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Tooltip title="Sil">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <>
      <div className="page-wrapper vh-100 d-flex flex-column justify-content-between">
        <div className="content flex-fill h-100">
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Araç Yönetimi</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Saha Yönetimi</li>
                  <li className="breadcrumb-item active">Araçlar</li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setCreateModalVisible(true)}
                size="large"
              >
                Yeni Araç Ekle
              </Button>
            </div>
          </div>

          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            <Col flex="1 1 180px">
              <Card>
                <Statistic
                  title="Toplam Araç"
                  value={stats.total}
                  prefix={<CarOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col flex="1 1 180px">
              <Card>
                <Statistic
                  title="Atanabilir"
                  value={stats.available}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col flex="1 1 180px">
              <Card>
                <Statistic
                  title="Zimmetli"
                  value={stats.assigned}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col flex="1 1 180px">
              <Card>
                <Statistic
                  title="Havuz Aracı"
                  value={stats.pool}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
            </Col>
            <Col flex="1 1 180px">
              <Card>
                <Statistic
                  title="Atanamaz"
                  value={stats.maintenance}
                  valueStyle={{ color: '#faad14' }}
                />
              </Card>
            </Col>
          </Row>

          <Card style={{ marginBottom: '16px' }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} lg={8}>
                <Input
                  placeholder="Araç ara..."
                  prefix={<SearchOutlined />}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  allowClear
                />
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <Select
                  placeholder="Durum filtrele"
                  value={statusFilter}
                  onChange={setStatusFilter}
                  style={{ width: '100%' }}
                  allowClear
                >
                  <Option value={VehicleStatus.ACTIVE}>Atanabilir</Option>
                  <Option value={VehicleStatus.PASSIVE}>Atanamaz</Option>
                  <Option value={VehicleStatus.SCRAPPED}>Hurdaya Ayrıldı</Option>
                  <Option value={VehicleStatus.DELETED}>Silindi</Option>
                </Select>
              </Col>
              <Col xs={24} sm={12} lg={8}>
                <Select
                  placeholder="Tip filtrele"
                  value={typeFilter}
                  onChange={setTypeFilter}
                  style={{ width: '100%' }}
                  allowClear
                >
                  <Option value={VehicleType.CAR}>Otomobil</Option>
                  <Option value={VehicleType.VAN}>Minibüs</Option>
                  <Option value={VehicleType.MOTORCYCLE}>Motosiklet</Option>
                  <Option value={VehicleType.TRUCK}>Kamyon</Option>
                </Select>
              </Col>
            </Row>
            <div style={{ marginTop: '16px', textAlign: 'right' }}>
              <Button
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter(undefined);
                  setTypeFilter(undefined);
                }}
                icon={<FilterOutlined />}
              >
                Filtreleri Temizle
              </Button>
            </div>
          </Card>

          <Card>
            {loading ? (
              <LoadingSpinner centered />
            ) : (
              <Table
                columns={columns}
                dataSource={filteredVehicles}
              
                rowKey="id"
                pagination={{
                  pageSize: 10,
                  showTotal: (total) => `Toplam ${total} araç`
                }}
              />
            )}
          </Card>
        </div>

        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <FooterSection />
        </div>
      </div>

      {/* Create Vehicle Modal */}
      <Modal
        title="Yeni Araç Ekle"
        open={createModalVisible}
        onCancel={() => {
          setCreateModalVisible(false);
          createForm.resetFields();
        }}
        footer={null}
        width={700}
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreateVehicle}>
          <Form.Item
            name="name"
            label="Araç Adı"
            rules={[{ required: true, message: 'Lütfen araç adı girin!' }]}
          >
            <Input placeholder="Örn: Servis Aracı 1" />
          </Form.Item>

          <Form.Item
            name="plateNumber"
            label="Plaka"
            rules={[{ required: true, message: 'Lütfen plaka girin!' }]}
          >
            <Input placeholder="34 ABC 123" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="brand"
                label="Marka"
                rules={[{ required: true, message: 'Lütfen marka girin!' }]}
              >
                <Input placeholder="Ford" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="model"
                label="Model"
                rules={[{ required: true, message: 'Lütfen model girin!' }]}
              >
                <Input placeholder="Transit" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="year"
                label="Yıl"
                rules={[{ required: true, message: 'Lütfen yıl girin!' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  min={1900}
                  max={new Date().getFullYear() + 1}
                  placeholder="2023"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="type"
                label="Araç Tipi"
                rules={[{ required: true, message: 'Lütfen araç tipi seçin!' }]}
              >
                <Select placeholder="Tip seçin">
                  <Option value={VehicleType.CAR}>Otomobil</Option>
                  <Option value={VehicleType.VAN}>Minibüs</Option>
                  <Option value={VehicleType.MOTORCYCLE}>Motosiklet</Option>
                  <Option value={VehicleType.TRUCK}>Kamyon</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="assignmentType"
            label="Kullanım Tipi"
            initialValue={VehicleAssignmentType.PERSONAL}
            extra={POOL_HELPER_TEXT}
          >
            <Radio.Group>
              <Radio value={VehicleAssignmentType.PERSONAL}>Kişisel (zimmetli)</Radio>
              <Radio value={VehicleAssignmentType.POOL}>Havuz aracı</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setCreateModalVisible(false)}>İptal</Button>
              <Button type="primary" htmlType="submit">
                Ekle
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Vehicle Modal */}
      <Modal
        title="Araç Düzenle"
        open={editModalVisible}
        onCancel={() => {
          setEditModalVisible(false);
          editForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form form={editForm} layout="vertical" onFinish={handleUpdateVehicle}>
          <Form.Item
            name="name"
            label="Araç Adı"
            rules={[{ required: true, message: 'Lütfen araç adı girin!' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="plateNumber"
            label="Plaka"
            rules={[{ required: true, message: 'Lütfen plaka girin!' }]}
          >
            <Input />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="brand"
                label="Marka"
                rules={[{ required: true, message: 'Lütfen marka girin!' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="model"
                label="Model"
                rules={[{ required: true, message: 'Lütfen model girin!' }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="year"
                label="Yıl"
                rules={[{ required: true, message: 'Lütfen yıl girin!' }]}
              >
                <InputNumber style={{ width: '100%' }} min={1900} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="status"
                label="Durum"
                rules={[{ required: true, message: 'Lütfen durum seçin!' }]}
                >
                  <Select>
                    <Option value={VehicleStatus.ACTIVE}>Atanabilir</Option>
                    <Option value={VehicleStatus.PASSIVE}>Atanamaz</Option>
                    <Option value={VehicleStatus.SCRAPPED}>Hurdaya Ayrıldı</Option>
                  </Select>
                </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="assignmentType"
            label="Kullanım Tipi"
            extra={
              selectedVehicle?.currentAssignment && !isPoolVehicle(selectedVehicle)
                ? 'Zimmetli araç havuza alınamaz; önce aracın iadesini alın.'
                : POOL_HELPER_TEXT
            }
          >
            <Radio.Group>
              <Radio value={VehicleAssignmentType.PERSONAL}>Kişisel (zimmetli)</Radio>
              <Radio
                value={VehicleAssignmentType.POOL}
                disabled={!!selectedVehicle?.currentAssignment && !isPoolVehicle(selectedVehicle)}
              >
                Havuz aracı
              </Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setEditModalVisible(false)}>İptal</Button>
              <Button type="primary" htmlType="submit">
                Güncelle
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Assign Vehicle Modal */}
      <Modal
        title="Aracı Zimmetle"
        open={assignModalVisible}
        onCancel={() => {
          setAssignModalVisible(false);
          assignForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form form={assignForm} layout="vertical" onFinish={handleAssignVehicle}>
          <Form.Item
            name="assigneeId"
            label="Zimmetlenecek Kişi"
            rules={[{ required: true, message: 'Lütfen kişi seçin!' }]}
          >
            <Select
              placeholder="Kişi seçin"
              showSearch
              filterOption={(input, option) =>
                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
              }
              options={assignableUsers.map((assignableUser) => ({
                value: assignableUser.id,
                label: `${assignableUser.firstName} ${assignableUser.lastName}`
              }))}
            />
          </Form.Item>

          <Form.Item
            name="odometer"
            label="Kilometre Bilgisi"
            rules={[{ required: true, message: 'Lütfen kilometre bilgisi girin!' }]}
          >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                placeholder="15000"
                addonAfter="km"
              />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setAssignModalVisible(false)}>İptal</Button>
              <Button type="primary" htmlType="submit">
                Zimmetle
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Details Modal */}
      <Modal
        title="Araç Detayları"
        open={detailsModalVisible}
        onCancel={() => {
          setDetailsModalVisible(false);
          setSelectedVehicle(null);
        }}
        footer={null}
        width={700}
      >
        {selectedVehicle && (
          <div>
            <Descriptions bordered column={2}>
              <Descriptions.Item label="Araç Adı" span={2}>
                {selectedVehicle.name}
              </Descriptions.Item>
              <Descriptions.Item label="Plaka">
                {selectedVehicle.plateNumber}
              </Descriptions.Item>
              <Descriptions.Item label="Durum">
                {getStatusTag(selectedVehicle.status)}
              </Descriptions.Item>
              <Descriptions.Item label="Marka">
                {selectedVehicle.brand}
              </Descriptions.Item>
              <Descriptions.Item label="Model">
                {selectedVehicle.model}
              </Descriptions.Item>
              <Descriptions.Item label="Yıl">
                {selectedVehicle.year}
              </Descriptions.Item>
              <Descriptions.Item label="Tip">
                {getVehicleTypeLabel(selectedVehicle.type)}
              </Descriptions.Item>
              <Descriptions.Item label="Kullanım Tipi" span={2}>
                {isPoolVehicle(selectedVehicle) ? <Tag color="purple">Havuz</Tag> : 'Kişisel (zimmetli)'}
              </Descriptions.Item>
              {selectedVehicle.currentOdometer && (
                <Descriptions.Item label="Kilometre" span={2}>
                  {selectedVehicle.currentOdometer.toLocaleString()} km
                </Descriptions.Item>
              )}
            </Descriptions>

            {selectedVehicle.currentAssignment && (
              <div style={{ marginTop: '24px' }}>
                <Title level={5}>Mevcut Zimmet</Title>
                <Card size="small" style={{ background: '#e6f7ff', borderColor: '#91d5ff' }}>
                  <Descriptions column={2}>
                    <Descriptions.Item label="Zimmetli Kişi" span={2}>
                      {(() => {
                        const assignee = selectedVehicle.currentAssignment.assignee || selectedVehicle.currentAssignment.assignedTo;
                        return assignee ? (
                          <Text strong style={{ color: '#1890ff' }}>
                            {assignee.firstName} {assignee.lastName}
                          </Text>
                        ) : '-';
                      })()}
                    </Descriptions.Item>
                    <Descriptions.Item label="Zimmet Tarihi">
                      {dayjs(selectedVehicle.currentAssignment.assignedAt).format(
                        'DD.MM.YYYY HH:mm'
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label="Başlangıç Km">
                      {selectedVehicle.currentAssignment.odometerOnAssignment.toLocaleString()} km
                    </Descriptions.Item>
                  </Descriptions>
                </Card>
              </div>
            )}
            
            {!selectedVehicle.currentAssignment && (
              <div style={{ marginTop: '24px' }}>
                <Card size="small" style={{ background: '#f6ffed', borderColor: '#b7eb8f' }}>
                  <Text type="secondary">
                    <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '8px' }} />
                    {isPoolVehicle(selectedVehicle)
                      ? 'Bu bir havuz aracıdır; kimseye zimmetlenmez.'
                      : 'Bu araç şu anda kimseye zimmetli değil.'}
                  </Text>
                </Card>
              </div>
            )}

            {/* Araç Geçmişi Bölümü */}
            {(selectedVehicle as any)?.assignments && (selectedVehicle as any).assignments.length > 0 && (
              <div style={{ marginTop: '24px' }}>
                <Title level={5}>Araç Geçmişi</Title>
                <List
                  itemLayout="horizontal"
                  dataSource={(selectedVehicle as any).assignments}
                  renderItem={(item: any) => (
                    <List.Item>
                      <List.Item.Meta
                        avatar={<Avatar icon={<UserOutlined />} />}
                        title={`${item.assignedTo?.firstName} ${item.assignedTo?.lastName}`}
                        description={
                          <>
                            <div>{`Atama: ${dayjs(item.assignmentDate).format('DD.MM.YYYY HH:mm')}` + (item.returnDate ? ` - İade: ${dayjs(item.returnDate).format('DD.MM.YYYY HH:mm')}` : '')}</div>
                            <div>
                              {`Teslim Km: ${item.odometerOnAssignment}`}
                              {item.odometerOnReturn ? ` - İade Km: ${item.odometerOnReturn}` : ''}
                            </div>
                          </>
                        }
                      />
                       {!item.returnDate && (
                         <Tag color="blue">Halen Zimmetli</Tag>
                       )}
                    </List.Item>
                  )}
                />
              </div>
            )}

            {/* Kullanım Geçmişi (aktivitelerde kullanım) */}
            <div style={{ marginTop: '24px' }}>
              <Title level={5}>Kullanım Geçmişi</Title>
              <Table<VehicleUsageHistoryItem>
                size="small"
                rowKey="activityId"
                columns={USAGE_HISTORY_COLUMNS}
                dataSource={selectedVehicle.usageHistory || []}
                loading={detailsLoading}
                pagination={
                  (selectedVehicle.usageHistory?.length || 0) > 10 ? { pageSize: 10, size: 'small' } : false
                }
                locale={{ emptyText: 'Bu araç henüz bir aktivitede kullanılmadı.' }}
                scroll={{ x: 'max-content' }}
              />
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default VehiclesPage;
